import React, { MouseEventHandler, useEffect, useMemo, useState } from "react";
import { Connection, ConnectionConfiguration, Member, Provider, ProviderType } from "../common/ObjectTypes";
import SchemaForm from "../common/SchemaForm";
import { CopyToClipboard, HideString, ShowString } from "../common/ProjectIcons";
import { getButtonText, getResponseText } from "../common/TestActionFunctions";
import { useTranslation } from "react-i18next";
import { isActionVisible } from "../common/SchemaFormTemplates/checkSchema";
import { filterConfigurableActions } from "../common/SchemaFormTemplates/actionFilters";
import { inputClasses, buttonClasses } from "../common/tailwindClassnames";
import TestAction from "../common/TestAction";
import { toast } from "react-toastify";
import { getEnvironment } from "../common/getEnvironment";
import { useHelp, HelpCard } from "../../../context/HelpContext";
import { getConnectionKind, providerHasPushAction } from "../common/connectionKind";
import { CodeSampleSwitcher } from "./codeSamples";

const TOAST_DURATION_MS = 2000;
const WEBHOOK_EDITOR_HEIGHT = "280px";
const API_PUSH_EDITOR_HEIGHT = "240px";

type ConnectionDetailsProviderProps = {
    connection: Connection,
    currentMemberIsAdmin: boolean,
    rsjfErrors: any,
    apiKey: string,
    connectionProvider: Provider,
    selectedProviderType: ProviderType,
    onChangeProviderEndpoint: Function,
    onChangeProviderConfigurations: Function,
    onTestAction: Function,
    testActionLoading: boolean,
    testActionResponse: any,
    testActionStatus: number,
    onUpdateConnection: MouseEventHandler,
    onCancelConnectionChanges: MouseEventHandler,
    changesMade: boolean,
    authToken?: string,
}

const ConnectionDetailsProvider = ({
    connection,
    currentMemberIsAdmin,
    rsjfErrors,
    apiKey,
    connectionProvider,
    selectedProviderType,
    onChangeProviderEndpoint,
    onChangeProviderConfigurations,
    onTestAction,
    testActionLoading,
    testActionResponse,
    testActionStatus,
    onUpdateConnection,
    onCancelConnectionChanges,
    changesMade,
    authToken,
}: ConnectionDetailsProviderProps) => {
    const { t, i18n } = useTranslation();
    const { setHelpContent } = useHelp();
    const [activeActionId, setActiveActionId] = useState(null);
    const [keyVisible, setKeyVisible] = useState(false);

    const observationsEndpoint = useMemo(() => {
        const rawEnv = getEnvironment();
        const env = rawEnv === 'local' ? 'dev' : rawEnv;
        return env === 'production'
            ? 'https://sensors.api.gundiservice.org/v2/observations/'
            : `https://sensors.api.${env}.gundiservice.org/v2/observations/`;
    }, []);

    const webhookEndpoint = useMemo(() => {
        const rawEnv = getEnvironment();
        const env = rawEnv === 'local' ? 'dev' : rawEnv;
        return env === 'production'
            ? 'https://hooks.gundiservice.org/webhooks'
            : `https://hooks.${env}.gundiservice.org/webhooks`;
    }, []);

    const connectionKind = getConnectionKind(selectedProviderType);
    const hasPushAction = providerHasPushAction(selectedProviderType);

    useEffect(() => {
        const cards: HelpCard[] = [
            {
                title: t('Setting up a Connection in Gundi'),
                description: t('Find step-by-step instructions for connecting your data source.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/routes-gundi/' },
            },
            ...(selectedProviderType.help_center_url ? [{
                title: t('{{technology}} Connection Set Up', { technology: selectedProviderType.name }),
                description: t('Configuration guide specific to {{technology}} connections.', { technology: selectedProviderType.name }),
                button: { label: t('Learn More'), url: selectedProviderType.help_center_url },
            }] : []),
            ...(connectionKind === 'webhook' ? [{
                title: t('Gundi Webhook API'),
                description: t('Learn how to send data to Gundi using webhooks.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/custom-webhook' },
            }] : []),
            ...((connectionKind === 'push-only' || hasPushAction) ? [{
                title: t('Gundi API'),
                description: t('Learn how to push data to Gundi using the API.'),
                button: { label: t('Learn More'), url: 'https://support.earthranger.com/developer_docs/gundi-api' },
            }] : []),
        ];
        setHelpContent({ cards });
    }, [selectedProviderType, connectionKind, i18n.language, t, setHelpContent]);

    useEffect(() => {
        return () => setHelpContent(null);
    }, [setHelpContent]);

    return (
        <div className="h-full mt-3 flex flex-row bg-very-light-gray">
            <div className="flex flex-col pb-16 w-full">
                {/* div with text */}
                {/* <div className="flex flex-row justify-between items-center w-full">
                    <label className="text-secondary-gray">{t("The following inputs connect Gundi to the account that provides the data.")}</label>
                </div> */}
                {/* div with configuration details */}
                <div className="flex flex-col w-full pb-52">
                    {/* endpoint, dynamic form */}
                    <div className="flex flex-row">
                        {/* endpoint */}
                        <div className="flex flex-col w-full mr-3 mb-3">
                            {/* <div className="font-bold text-secondary-gray ">Configurations</div> */}
                            <div className="w-full">
                                {/* API Key + instructions - shown for push-only, webhook, and standard providers that also have a push action */}
                                {(connectionKind !== 'standard' || hasPushAction) && (
                                    // Webhook takes precedence: a provider that is both webhook and push-action should render the webhook UI
                                    connectionKind === 'webhook'
                                        ? (
                                            <>
                                            <div className="mb-4 rounded p-4">
                                                <div className="mb-4 flex flex-col items-start">
                                                    <h2 className="text-2xl font-normal text-secondary-gray transition-all duration-300">{t("How to Connect")}</h2>
                                                </div>
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <p className="text-base text-off-black mb-6">
                                                        {t("Your API key is ready. To start sending data, configure your source system to POST to the Gundi webhook endpoint below. You'll need to include your API key and a header that identifies the integration type with every request.")}
                                                    </p>

                                                    {/* Config table: endpoint + headers */}
                                                    <div className="mb-6 max-w-2xl rounded border border-field-outline bg-very-light-gray overflow-hidden">
                                                        {/* Endpoint row */}
                                                        <div className="flex items-center justify-between gap-4 px-4 py-3 border-b border-divider-lines">
                                                            <div className="flex flex-col min-w-0">
                                                                <span className="text-xs text-secondary-gray mb-0.5">{t("Endpoint")}</span>
                                                                <span className="font-mono text-sm text-off-black truncate">{webhookEndpoint}</span>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                onClick={() => {
                                                                    navigator.clipboard.writeText(webhookEndpoint)
                                                                        .then(() => toast.info(t("Endpoint copied to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                        .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                }}
                                                                className="text-secondary-gray hover:text-off-black flex-shrink-0"
                                                                aria-label={t("Copy webhook endpoint")}
                                                            >
                                                                <CopyToClipboard />
                                                            </button>
                                                        </div>
                                                        {/* Column labels */}
                                                        <div className="grid grid-cols-2 border-b border-divider-lines bg-very-light-gray px-4 py-2">
                                                            <span className="text-xs text-secondary-gray">{t("Header")}</span>
                                                            <span className="text-xs text-secondary-gray">{t("Value")}</span>
                                                        </div>
                                                        {/* apikey row */}
                                                        <div className="grid grid-cols-2 border-b border-divider-lines">
                                                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                                                <span className="font-mono text-sm text-off-black">apikey</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText("apikey")
                                                                            .then(() => toast.info(t("Header name copied to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                            .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                    }}
                                                                    className="text-secondary-gray hover:text-off-black flex-shrink-0"
                                                                    aria-label={t("Copy header name")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            </div>
                                                            <div className="flex items-center justify-between gap-2 px-4 py-3">
                                                                <span className="font-mono text-sm text-off-black flex-1 truncate">
                                                                    {keyVisible ? apiKey : '••••••••••••••••'}
                                                                </span>
                                                                <div className="flex items-center gap-1 flex-shrink-0">
                                                                    {currentMemberIsAdmin && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setKeyVisible(!keyVisible)}
                                                                            className="text-secondary-gray hover:text-off-black"
                                                                            aria-label={keyVisible ? t("Hide API key") : t("Show API key")}
                                                                        >
                                                                            {keyVisible ? <HideString /> : <ShowString />}
                                                                        </button>
                                                                    )}
                                                                    {currentMemberIsAdmin && (
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => {
                                                                                navigator.clipboard.writeText(apiKey)
                                                                                    .then(() => toast.info(`${t("API Key")} ${t("copied to clipboard")}`, { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                                    .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                            }}
                                                                            className="text-secondary-gray hover:text-off-black"
                                                                            aria-label={t("Copy API key")}
                                                                        >
                                                                            <CopyToClipboard />
                                                                        </button>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                        {/* x-gundi-integration-type row */}
                                                        <div className="grid grid-cols-2">
                                                            <div className="flex items-center justify-between gap-2 px-4 py-3 border-r border-divider-lines">
                                                                <span className="font-mono text-sm text-off-black">x-gundi-integration-type</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText("x-gundi-integration-type")
                                                                            .then(() => toast.info(t("Header name copied to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                            .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                    }}
                                                                    className="text-secondary-gray hover:text-off-black flex-shrink-0"
                                                                    aria-label={t("Copy header name")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            </div>
                                                            <div className="flex items-center justify-between gap-2 px-4 py-3">
                                                                <span className="font-mono text-sm text-off-black">{selectedProviderType.value}</span>
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(selectedProviderType.value)
                                                                            .then(() => toast.info(t("Header value copied to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                            .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                    }}
                                                                    className="text-secondary-gray hover:text-off-black flex-shrink-0"
                                                                    aria-label={t("Copy header value")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                            <div className="mb-4 rounded p-4">
                                                <div className="mb-4 flex flex-col items-start">
                                                    <h2 className="text-2xl font-normal text-secondary-gray transition-all duration-300">{t("For Developers")}</h2>
                                                </div>
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <p className="text-base text-off-black mb-4">
                                                        {t("Share this sample code with any developers integrating with this connection. Every request must include the apikey and x-gundi-integration-type headers, a timestamp preferably in ISO 8601 format, latitude and longitude, and a device or source ID.")}
                                                    </p>
                                                    <CodeSampleSwitcher
                                                        kind="webhook"
                                                        endpoint={webhookEndpoint}
                                                        apiKey={keyVisible ? apiKey : null}
                                                        integrationType={selectedProviderType.value}
                                                        height={WEBHOOK_EDITOR_HEIGHT}
                                                    />
                                                    <p className="text-base text-off-black mt-4">
                                                        <a
                                                            href="https://support.earthranger.com/developer_docs/custom-webhook"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-blue-basic underline"
                                                        >
                                                            {t("Learn more about Gundi's webhook API.")}
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                            </>
                                        ) : (
                                            <>
                                            <div className="mb-4 rounded p-4">
                                                <div className="mb-4 flex flex-col items-start">
                                                    <h2 className="text-2xl font-normal text-secondary-gray transition-all duration-300">{t("How to Connect")}</h2>
                                                </div>
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <p className="text-base text-off-black mb-6">
                                                        {t("Your API key is ready. An API key is a unique credential that lets your data provider securely authenticate and send data to this connection. Copy it below and share it with them — if their system has a Gundi integration, paste the key there to complete the setup.")}
                                                    </p>

                                                    {/* API Key field */}
                                                    <div className="flex flex-col mb-6 max-w-xl">
                                                        <label className="inline-block hover:cursor-pointer text-off-black text-sm mb-1">
                                                            {t("API Key")}
                                                        </label>
                                                        <div className="flex flex-row items-center gap-2">
                                                            <div className="relative flex-1 min-w-0">
                                                                <input
                                                                    id="apikey"
                                                                    value={apiKey}
                                                                    className={`${inputClasses.disabled} pr-10 w-full`}
                                                                    type={keyVisible ? "text" : "password"}
                                                                    readOnly={true}
                                                                />
                                                                {currentMemberIsAdmin && (
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => setKeyVisible(!keyVisible)}
                                                                        className="absolute inset-y-0 right-0 flex items-center pr-3 text-off-black focus:outline-none"
                                                                        aria-label={keyVisible ? t("Hide API key") : t("Show API key")}
                                                                    >
                                                                        {keyVisible ? <HideString /> : <ShowString />}
                                                                    </button>
                                                                )}
                                                            </div>
                                                            {currentMemberIsAdmin && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => {
                                                                        navigator.clipboard.writeText(apiKey)
                                                                            .then(() => toast.info(`${t("API Key")} ${t("copied to clipboard")}`, { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }))
                                                                            .catch(() => toast.error(t("Failed to copy to clipboard"), { position: toast.POSITION.TOP_RIGHT, autoClose: TOAST_DURATION_MS }));
                                                                    }}
                                                                    className="flex h-full items-center justify-center rounded border border-neutral-gray-300 px-3 text-off-black flex-shrink-0"
                                                                    aria-label={t("Copy API key")}
                                                                >
                                                                    <CopyToClipboard />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>

                                                </div>
                                            </div>
                                            <div className="mb-4 rounded p-4">
                                                <div className="mb-4 flex flex-col items-start">
                                                    <h2 className="text-2xl font-normal text-secondary-gray transition-all duration-300">{t("For Developers")}</h2>
                                                </div>
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <p className="text-base text-off-black mb-4">
                                                        {t("Share this sample code and API key with any developers who need to push data to this connection. They can use it as a starting point to integrate their system directly.")}
                                                    </p>
                                                    <CodeSampleSwitcher
                                                        kind="api-push"
                                                        endpoint={observationsEndpoint}
                                                        apiKey={keyVisible ? apiKey : null}
                                                        integrationType={selectedProviderType.value}
                                                        height={API_PUSH_EDITOR_HEIGHT}
                                                    />
                                                    <p className="text-base text-off-black mt-4">
                                                        <a
                                                            href="https://support.earthranger.com/developer_docs/gundi-api"
                                                            target="_blank"
                                                            rel="noreferrer"
                                                            className="text-blue-basic underline"
                                                        >
                                                            {t("Learn more about Gundi's API.")}
                                                        </a>
                                                    </p>
                                                </div>
                                            </div>
                                            </>
                                        )
                                )}

                                {/* schema form */}
                                {filterConfigurableActions(selectedProviderType.actions, "push").map((item: any) => {
                                    const uiSchema = item.ui_schema ? { ...item.ui_schema } : {};
                                    const formTitle = item.ui_schema && item.ui_schema['ui:title'] ? item.ui_schema['ui:title'] : item.name;
                                    return (
                                        <div key={item.id} className={isActionVisible(item) ? "mb-[16px] rounded p-[16px]" : "hidden"}>
                                            <div className="mb-4 flex flex-col items-start">
                                                {item.schema.hasOwnProperty('is_executable') && item.schema.is_executable === true ? (
                                                    <TestAction
                                                        actionId={item.id}
                                                        actionValue={item.value}
                                                        onTestAction={(id: any, value: any) => {
                                                            onTestAction(id, value);
                                                            setActiveActionId(id);
                                                        }}
                                                        isLoading={testActionLoading && activeActionId === item.id}
                                                        response={activeActionId === item.id ? testActionResponse : null}
                                                        buttonText={getButtonText(item.value, t)}
                                                        formTitle={formTitle}
                                                        uiSchema={uiSchema}
                                                    />
                                                ) : (
                                                    <div className="text-2xl font-normal text-secondary-gray transition-all duration-300">{formTitle}</div>
                                                )}
                                                {uiSchema['ui:description'] && (
                                                    <div className="form-root-description !mb-0 text-gray-600 mt-1 transition-colors duration-300">
                                                        {uiSchema['ui:description']}
                                                    </div>
                                                )}
                                            </div>
                                            {item?.schema?.properties && Object.keys(item.schema.properties).length > 0 && (
                                                <div className="rounded border border-field-outline bg-white p-[16px] transition-colors duration-300">
                                                    <SchemaForm
                                                        userIsAdmin={currentMemberIsAdmin}
                                                        formTitle=""
                                                        schema={item.schema}
                                                        ui_schema={{...uiSchema, 'ui:description': ''}}
                                                        formdata={connectionProvider.configurations && connectionProvider.configurations.findIndex((a: ConnectionConfiguration) => a.action.id === item.id) !== -1 ? connectionProvider.configurations.find((a: ConnectionConfiguration) => a.action.id === item.id).data : {}}
                                                        onChange={(e: any) => onChangeProviderConfigurations(e, item.id)}
                                                        isEditable={true}
                                                        errors={rsjfErrors[item.id]}
                                                        integrationId={connectionProvider.id}
                                                        authToken={authToken}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}


                            </div>
                        </div>
                    </div>

                </div>
            </div>
            {/* buttons */}
            <div className="w-full z-10 fixed bottom-0 mt-12 mr-6 py-3 w-full flex flex-row gap-3 bg-white/10 backdrop-blur">
                <button className={buttonClasses.primary}
                    disabled={!changesMade}
                    onClick={onUpdateConnection}>
                    Save
                </button>
                <button className={buttonClasses.cancel}
                    onClick={onCancelConnectionChanges}
                >
                    Cancel
                </button>
            </div>
        </div>
    )
};

export default ConnectionDetailsProvider;